import { useEffect, useState } from 'react';

/**
 * Admin control for the weekly safety digest email.
 *
 * The digest also fires on a weekly schedule (server-side); this is for seeing
 * exactly what a customer receives and for sending on demand. When SMTP is not
 * configured the send is a safe no-op — the result says so, and Preview still
 * shows the real HTML.
 */

interface Cust { id: number; name: string }

export function SafetyDigestPanel() {
  const [customers, setCustomers] = useState<Cust[]>([]);
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch('/api/customers').then((r) => (r.ok ? r.json() : [])).then(setCustomers).catch(() => undefined);
  }, []);

  const doPreview = async () => {
    if (!customerId) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch('/api/safety/digest/preview', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ customerId }),
      });
      const b = await res.json();
      setPreview(res.ok ? b.html : `Error: ${b.message ?? res.status}`);
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    if (!confirm('Send the weekly digest to every eligible customer now?')) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch('/api/safety/digest/send', { method: 'POST' });
      const b = await res.json();
      setResult(res.ok ? `${b.attempted} eligible · ${b.sent} sent · ${b.skipped} skipped` : `Error: ${b.message ?? res.status}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <strong>Weekly digest email</strong>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 10px' }}>
        A per-customer weekly safety summary to their billing email. Sends automatically each week
        (when enabled on the server); preview or send on demand here.
      </p>
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : '')} style={{ fontSize: 13 }}>
          <option value="">— pick a customer —</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn sm" disabled={!customerId || busy} onClick={() => void doPreview()}>Preview</button>
        <button className="btn sm primary" disabled={busy} onClick={() => void sendNow()}>Send now to all</button>
        {result && <span className="muted" style={{ fontSize: 12 }}>{result}</span>}
      </div>
      {preview && (
        <div style={{ marginTop: 12 }}>
          {preview.startsWith('Error') ? (
            <div style={{ color: 'var(--bad)', fontSize: 13 }}>{preview}</div>
          ) : (
            <iframe title="digest preview" srcDoc={preview} style={{ width: '100%', height: 420, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }} />
          )}
        </div>
      )}
    </div>
  );
}
